import { useState } from 'react'
import { UserPlus, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { useCreatePlayer, useGhinLookup } from '../../hooks/usePlayers'
import { useAuth } from '../../contexts/AuthContext'
import { getInitials } from '../../utils/colors'
import { combineHandicapInput, splitHandicapForInput } from '../../utils/handicaps'
import { AVATAR_COLORS } from './constants'
import PlusHandicapToggle from './PlusHandicapToggle'

/**
 * Collapsible manual player creation form with name, email, GHIN lookup,
 * handicap, gender selector, and avatar color picker.
 *
 * @param {Object} props
 * @param {Function} props.onPlayerCreated - Called with the new player object after creation
 * @param {Set} props.excludeIds - Player IDs to exclude from dedup suggestions
 * @param {Array} props.allPlayers - All players for dedup suggestions
 * @param {boolean} [props.collapsed] - Controlled collapsed state (optional)
 * @param {Function} [props.onToggle] - Called when accordion is toggled (optional)
 * @param {Function} [props.onDedupAdd] - Called when user clicks a dedup suggestion to add existing player
 */
export default function ManualPlayerForm({ onPlayerCreated, excludeIds, allPlayers, collapsed, onToggle, onDedupAdd }) {
  const { user } = useAuth()
  const createPlayer = useCreatePlayer()
  const ghinLookup = useGhinLookup()

  // If collapsed/onToggle not provided, manage internally
  const [internalCollapsed, setInternalCollapsed] = useState(true)
  const isCollapsed = collapsed !== undefined ? collapsed : internalCollapsed
  const handleToggle = onToggle || (() => setInternalCollapsed((v) => !v))

  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [manualGhin, setManualGhin] = useState('')
  const [manualHandicap, setManualHandicap] = useState('')
  const [manualIsPlus, setManualIsPlus] = useState(false)
  const [manualGender, setManualGender] = useState('M')
  const [manualColor, setManualColor] = useState(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)])

  // Dedup suggestions: manual players created by this user that match the typed name
  const manualDedupSuggestions = manualName.trim().length >= 2
    ? (allPlayers || []).filter((p) =>
        !p.user_id &&
        p.created_by === user?.id &&
        !excludeIds.has(p.id) &&
        p.display_name?.toLowerCase().includes(manualName.trim().toLowerCase())
      )
    : []

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!manualName.trim()) return

    const handicapValue = combineHandicapInput({ isPlus: manualIsPlus, magnitude: manualHandicap })

    createPlayer.mutate(
      {
        display_name: manualName.trim(),
        email: manualEmail.trim() || undefined,
        avatar_color: manualColor,
        ...(manualGhin.trim() ? { ghin_number: manualGhin.trim() } : {}),
        ...(handicapValue != null ? { handicap_index: handicapValue } : {}),
        gender: manualGender,
      },
      {
        onSuccess: (newPlayer) => {
          onPlayerCreated(newPlayer)
          setManualName('')
          setManualEmail('')
          setManualGhin('')
          setManualHandicap('')
          setManualIsPlus(false)
          setManualGender('M')
          setManualColor(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)])
          if (onToggle) onToggle()
          else setInternalCollapsed(true)
        },
      }
    )
  }

  return (
    <div style={{
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '0.75rem',
      border: '1px solid var(--border-color)',
      overflow: 'hidden',
    }}>
      <button
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.375rem',
          width: '100%',
          padding: '0.75rem',
          fontSize: '0.8125rem',
          fontWeight: '600',
          color: 'var(--text-secondary)',
        }}
      >
        <UserPlus size={16} />
        Add Player Manually
        {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} style={{ padding: '0 1rem 1rem' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Name
            </label>
            <input
              type="text"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="e.g. Tiger"
              autoFocus
              required
              style={{
                width: '100%',
                height: '40px',
                padding: '0 0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {manualDedupSuggestions.length > 0 && (
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.625rem',
              borderRadius: '0.375rem',
              backgroundColor: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}>
              <p style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                You already created:
              </p>
              {manualDedupSuggestions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onDedupAdd?.(p.id)
                    setManualName('')
                    setManualEmail('')
                    if (onToggle) onToggle()
                    else setInternalCollapsed(true)
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem',
                    borderRadius: '9999px',
                    marginBottom: '0.25rem',
                    textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: p.avatar_color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.625rem',
                    color: '#fff',
                    flexShrink: 0,
                  }}>
                    {getInitials(p)}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                    {p.display_name}
                  </span>
                  <span style={{
                    fontSize: '0.6875rem',
                    fontWeight: '600',
                    color: '#f59e0b',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '1rem',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    flexShrink: 0,
                  }}>
                    Add
                  </span>
                </button>
              ))}
            </div>
          )}

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Email (optional)
            </label>
            <input
              type="email"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              placeholder="e.g. tiger@golf.com"
              style={{
                width: '100%',
                height: '40px',
                padding: '0 0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
              }}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.375rem', fontStyle: 'italic' }}>
              Adding their email lets them find their scores and claim their account
            </p>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              GHIN Number (optional)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={manualGhin}
                onChange={(e) => setManualGhin(e.target.value)}
                placeholder="e.g. 1234567"
                style={{
                  flex: 1,
                  height: '40px',
                  padding: '0 0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (!manualGhin.trim()) return
                  ghinLookup.mutate(manualGhin.trim(), {
                    onSuccess: (data) => {
                      if (data.handicap_index != null) {
                        const split = splitHandicapForInput(data.handicap_index)
                        setManualHandicap(split.magnitude)
                        setManualIsPlus(split.isPlus)
                      }
                      if (data.gender) setManualGender(data.gender)
                    },
                  })
                }}
                disabled={ghinLookup.isPending || !manualGhin.trim()}
                style={{
                  height: '40px',
                  padding: '0 0.625rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: 'var(--accent-primary)',
                  border: 'none',
                  cursor: ghinLookup.isPending || !manualGhin.trim() ? 'not-allowed' : 'pointer',
                  opacity: ghinLookup.isPending || !manualGhin.trim() ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={12} style={ghinLookup.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                Lookup
              </button>
            </div>
            {ghinLookup.isError && (
              <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                {ghinLookup.error?.message || 'GHIN lookup failed'}
              </p>
            )}
            {ghinLookup.isSuccess && (
              <p style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                Handicap updated from GHIN
              </p>
            )}
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Handicap Index (optional)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <PlusHandicapToggle isPlus={manualIsPlus} onChange={setManualIsPlus} />
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                value={manualHandicap}
                onChange={(e) => setManualHandicap(e.target.value)}
                placeholder="e.g. 12.4"
                style={{
                  flex: 1,
                  height: '40px',
                  padding: '0 0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
              Tap <strong>+ Plus</strong> for handicaps better than scratch (e.g. +1.4)
            </p>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Gender
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setManualGender(value)}
                  style={{
                    flex: 1, padding: '0.5rem', borderRadius: '0.5rem',
                    border: `1px solid ${manualGender === value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    backgroundColor: manualGender === value ? 'var(--accent-primary)' : 'var(--bg-primary)',
                    color: manualGender === value ? '#fff' : 'var(--text-primary)',
                    fontWeight: manualGender === value ? 600 : 400,
                    fontSize: '0.875rem', cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Color
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {AVATAR_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setManualColor(c)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: c,
                    border: manualColor === c ? '3px solid var(--text-primary)' : '3px solid transparent',
                  }}
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={!manualName.trim() || createPlayer.isPending}
            style={{
              width: '100%',
              height: '40px',
              fontSize: '0.875rem',
              borderRadius: '9999px',
              opacity: (!manualName.trim() || createPlayer.isPending) ? 0.5 : 1,
            }}
          >
            {createPlayer.isPending ? 'Adding...' : 'Create & Add'}
          </button>
        </form>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
